import React, { useState, useEffect, useMemo } from "react";
import "../../../assets/scss/components/ui/tables/InspectionDataTable.scss";

const InspectionDataTable = ({
  data,
  selectedInspections,
  onSelectInspection,
  theme,
  headerVisible,
  dataType = "vehicles",
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [activeTypeFilters, setActiveTypeFilters] = useState({
    TRACTOCAMION: true,
    "DOBLE TROQUE": true,
    TURBO: true,
    CAMIONETA: true,
    "CAMA ALTA": true,
    OTROS: true,
  });

  // Expandir automáticamente elementos duplicados al cargar
  useEffect(() => {
    const initialExpandedState = {};
    data.forEach((group) => {
      if (group.isDuplicate) {
        initialExpandedState[group.plate] = false;
      }
    });
    setExpandedGroups((prev) => ({ ...prev, ...initialExpandedState }));
  }, [data]);

  const toggleGroup = (plate, e) => {
    e.stopPropagation(); // Evitar propagación del evento
    setExpandedGroups((prev) => ({
      ...prev,
      [plate]: !prev[plate],
    }));
  };

  // Función para normalizar y verificar tipo de vehículo
  const getVehicleTypeCategory = (vehicleType) => {
    if (!vehicleType) return "OTROS";

    const normalizedType = vehicleType.trim().toUpperCase();

    if (
      normalizedType.includes("TRACTOCAMION") ||
      normalizedType === "TRACTO CAMION" ||
      normalizedType === "TRACTO"
    )
      return "TRACTOCAMION";
    if (
      normalizedType.includes("DOBLE TROQUE") ||
      normalizedType.includes("DOBLETROQUE")
    )
      return "DOBLE TROQUE";
    if (normalizedType.includes("TURBO")) return "TURBO";
    if (normalizedType.includes("CAMIONETA") || normalizedType === "CAMION")
      return "CAMIONETA";
    if (
      normalizedType.includes("CAMA ALTA") ||
      normalizedType === "CAMAALTA" ||
      normalizedType === "CAMA-ALTA"
    )
      return "CAMA ALTA";

    return "OTROS";
  };

  // Aplicar todos los filtros
  const filteredData = useMemo(() => {
    // Verificar si hay al menos un filtro de tipo activo
    const atLeastOneTypeActive = Object.values(activeTypeFilters).some(
      (v) => v === true
    );

    // Si ningún filtro está activo, no mostrar nada
    if (!atLeastOneTypeActive) {
      return [];
    }

    return data.filter((group) => {
      // Filtro de búsqueda por placa
      const matchesSearch = group.plate
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // Filtro de duplicados
      const matchesDuplicateFilter = showOnlyDuplicates
        ? group.isDuplicate
        : true;

      // Filtro por tipo de vehículo
      const vehicleType = group.records[0]?.vehicle_type || "";
      const category = getVehicleTypeCategory(vehicleType);
      const matchesTypeFilter = activeTypeFilters[category] === true;

      // El elemento debe coincidir con todos los filtros activos
      return matchesSearch && matchesDuplicateFilter && matchesTypeFilter;
    });
  }, [data, searchTerm, showOnlyDuplicates, activeTypeFilters]);

  // Calcular estadísticas de los resultados filtrados
  const filteredStats = useMemo(() => {
    const stats = {
      TRACTOCAMION: 0,
      "DOBLE TROQUE": 0,
      TURBO: 0,
      CAMIONETA: 0,
      "CAMA ALTA": 0,
      OTROS: 0,
      DUPLICADOS: 0,
      TOTAL: filteredData.length,
    };

    filteredData.forEach((group) => {
      // Contar duplicados
      if (group.isDuplicate) {
        stats.DUPLICADOS++;
      }

      // Determinar el tipo de vehículo del primer registro
      const vehicleType = group.records[0]?.vehicle_type || "";
      const category = getVehicleTypeCategory(vehicleType);

      // Incrementar contador según tipo
      if (stats.hasOwnProperty(category)) {
        stats[category]++;
      } else {
        stats.OTROS++;
      }
    });

    return stats;
  }, [filteredData]);

  const isSelected = (plate, record) => {
    return selectedInspections.some(
      (item) =>
        item.plate === plate && item.source_sheet === record.source_sheet
    );
  };

  // FUNCIÓN CORREGIDA: Ahora muestra solo el tipo seleccionado
  const toggleTypeFilter = (type) => {
    // Si el tipo ya está activo y es el único activo, no hacer nada
    if (
      activeTypeFilters[type] &&
      Object.entries(activeTypeFilters).filter(([_, value]) => value).length ===
        1
    ) {
      return;
    }

    // Crear un nuevo objeto con todos los tipos en false
    const newFilters = {};
    Object.keys(activeTypeFilters).forEach((key) => {
      newFilters[key] = false;
    });

    // Solo activar el tipo seleccionado
    newFilters[type] = true;

    setActiveTypeFilters(newFilters);
  };

  const toggleAllTypeFilters = (value) => {
    const newState = {};
    Object.keys(activeTypeFilters).forEach((key) => {
      newState[key] = value;
    });
    setActiveTypeFilters(newState);
  };

  const toggleDuplicatesFilter = () => {
    setShowOnlyDuplicates((prev) => !prev);
  };

  // Función para obtener valor seguro considerando ambas nomenclaturas
  const getSafeValue = (
    record,
    mainField,
    fallbackField = null,
    defaultValue = "N/A"
  ) => {
    // Verificar que record es un objeto válido
    if (!record || typeof record !== "object") {
      return defaultValue;
    }

    // Comprobar campo principal (incluye validación para strings vacíos)
    if (record[mainField] !== undefined && record[mainField] !== null) {
      const value = record[mainField];
      // Si es string vacío y queremos considerarlo como no válido
      if (typeof value === "string" && value.trim() === "") {
        // Continuar con el fallback
      } else {
        return value;
      }
    }

    // Comprobar campo alternativo
    if (
      fallbackField &&
      record[fallbackField] !== undefined &&
      record[fallbackField] !== null
    ) {
      const fallbackValue = record[fallbackField];
      if (typeof fallbackValue === "string" && fallbackValue.trim() === "") {
        // String vacío, continuar al valor por defecto
      } else {
        return fallbackValue;
      }
    }

    return defaultValue;
  };

  // Función para obtener nombre completo del operador
  const getOperatorFullName = (record) => {
    const firstName = getSafeValue(
      record,
      "operator_first_names",
      "operator_name",
      ""
    );
    const lastName = getSafeValue(
      record,
      "operator_last_names",
      "operator_last_name",
      ""
    );

    if (!firstName && !lastName) return "N/A";
    return `${firstName} ${lastName}`.trim();
  };

  // Función para renderizar valores booleanos
  const renderBooleanValue = (value) => {
    if (
      value === true ||
      value === "true" ||
      value === "SÍ" ||
      value === "SI" ||
      value === "YES" ||
      value === "X"
    ) {
      return "SÍ";
    }
    if (
      value === false ||
      value === "false" ||
      value === "NO" ||
      value === "."
    ) {
      return "NO";
    }
    return value || "N/A";
  };

  return (
    <div
      className={`inspection-table-container-${theme} ${
        !headerVisible ? "expanded" : ""
      }`}
    >
      <div
        className={`advanced-filters-${theme} ${
          headerVisible ? "visible" : "hidden"
        }`}
      >
        {/* Filtros y búsqueda */}
        <div className={`search-filters-${theme}`}>
          <input
            type="text"
            className={`search-input-${theme}`}
            placeholder="Buscar por placa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className={`filter-controls-${theme}`}>
            <label className={`filter-checkbox-${theme}`}>
              <input
                type="checkbox"
                checked={showOnlyDuplicates}
                onChange={toggleDuplicatesFilter}
              />
              <span>Solo duplicados</span>
            </label>
          </div>
        </div>

        {/* Filtros por tipo de vehículo */}
        <div className={`vehicle-type-filters-${theme}`}>
          <span className={`filter-label-${theme}`}>Tipos de vehículo:</span>
          <div className={`filter-buttons-${theme}`}>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters.TRACTOCAMION ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("TRACTOCAMION")}
            >
              Tractocamión
            </button>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters["DOBLE TROQUE"] ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("DOBLE TROQUE")}
            >
              Doble Troque
            </button>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters.TURBO ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("TURBO")}
            >
              Turbo
            </button>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters.CAMIONETA ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("CAMIONETA")}
            >
              Camioneta
            </button>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters["CAMA ALTA"] ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("CAMA ALTA")}
            >
              Cama Alta
            </button>
            <button
              className={`filter-button-${theme} ${
                activeTypeFilters.OTROS ? "active" : ""
              }`}
              onClick={() => toggleTypeFilter("OTROS")}
            >
              Otros
            </button>
            <div className={`filter-all-buttons-${theme}`}>
              <button
                className={`filter-all-button-${theme}`}
                onClick={() => toggleAllTypeFilters(true)}
              >
                Todos
              </button>
              <button
                className={`filter-all-button-${theme}`}
                onClick={() => toggleAllTypeFilters(false)}
              >
                Ninguno
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className={`vehicle-stats-${theme}`}>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.TOTAL}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.TRACTOCAMION}</span>
            <span className="stat-label">Tractocamión</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats["DOBLE TROQUE"]}</span>
            <span className="stat-label">Doble Troque</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.TURBO}</span>
            <span className="stat-label">Turbo</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.CAMIONETA}</span>
            <span className="stat-label">Camioneta</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats["CAMA ALTA"]}</span>
            <span className="stat-label">Cama Alta</span>
          </div>
          <div className={`stat-item-${theme}`}>
            <span className="stat-value">{filteredStats.OTROS}</span>
            <span className="stat-label">Otros</span>
          </div>
          <div className={`stat-item-${theme} stat-duplicates`}>
            <span className="stat-value">{filteredStats.DUPLICADOS}</span>
            <span className="stat-label">Duplicados</span>
          </div>
        </div>
      </div>

      <div
        className={`table-wrapper-${theme} ${!headerVisible ? "expanded" : ""}`}
      >
        <table className={`inspection-table-${theme}`}>
          <thead>
            <tr>
              <th>Placa</th>
              <th>Tipo</th>
              <th>Marca / Modelo</th>
              <th>Propietario</th>
              <th>Operador</th>
              <th>Hoja Excel</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((group) => (
              <React.Fragment key={group.plate}>
                <tr
                  className={`group-header-${theme} ${
                    group.isDuplicate ? "duplicate" : ""
                  }`}
                >
                  <td>
                    <div className={`plate-cell-${theme}`}>
                      <span
                        className="expand-icon"
                        onClick={(e) => toggleGroup(group.plate, e)}
                      >
                        {expandedGroups[group.plate] ? "▼" : "►"}
                      </span>
                      {group.plate}
                      {group.isDuplicate && (
                        <span className={`duplicate-badge-${theme}`}>
                          Duplicado
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{getSafeValue(group.records[0], "vehicle_type")}</td>
                  <td>{`${getSafeValue(
                    group.records[0],
                    "brand"
                  )} / ${getSafeValue(group.records[0], "model")}`}</td>
                  <td>{getSafeValue(group.records[0], "owner")}</td>
                  <td>{getOperatorFullName(group.records[0])}</td>
                  <td>
                    {group.isDuplicate
                      ? `${group.records.length} hojas encontradas`
                      : group.records[0]?.source_sheet}
                  </td>
                  <td>
                    {!group.isDuplicate && (
                      <button
                        className={`select-button-${theme} ${
                          isSelected(group.plate, group.records[0])
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          onSelectInspection(group.plate, group.records[0])
                        }
                      >
                        {isSelected(group.plate, group.records[0])
                          ? "Seleccionado"
                          : "Seleccionar"}
                      </button>
                    )}
                  </td>
                </tr>

                {expandedGroups[group.plate] &&
                  group.records.map((record, index) => (
                    <tr
                      key={`${group.plate}-${index}`}
                      className={`group-detail-${theme} ${
                        isSelected(group.plate, record) ? "selected" : ""
                      }`}
                    >
                      <td colSpan="7">
                        <div className={`record-details-${theme}`}>
                          {/* Información Principal del Vehículo */}
                          <div className={`record-section-${theme}`}>
                            <h4>Información del Vehículo</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">Tipo:</span>
                                <span className="value">
                                  {getSafeValue(record, "vehicle_type")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Marca:</span>
                                <span className="value">
                                  {getSafeValue(record, "brand")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Modelo:</span>
                                <span className="value">
                                  {getSafeValue(record, "model")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Fecha Matrícula:</span>
                                <span className="value">
                                  {formatDate(record.registration_date)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Licencia Tránsito:
                                </span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "transit_license_number"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Tonelaje:</span>
                                <span className="value">
                                  {getSafeValue(record, "tonnage")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Color:</span>
                                <span className="value">
                                  {getSafeValue(record, "color")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Clase Servicio:</span>
                                <span className="value">
                                  {getSafeValue(record, "service_type")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Propietario:</span>
                                <span className="value">
                                  {getSafeValue(record, "owner")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">NIT/CC:</span>
                                <span className="value">
                                  {getSafeValue(record, "owner_document")}
                                </span>
                              </div>
                            </div>

                            {/* Información del Remolque */}
                            <h4>Información del Remolque</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">Placa Remolque:</span>
                                <span className="value">
                                  {getSafeValue(record, "trailer_plate")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Modelo Remolque:</span>
                                <span className="value">
                                  {getSafeValue(record, "trailer_model")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Capacidad Total:</span>
                                <span className="value">
                                  {getSafeValue(record, "total_capacity")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Porcentaje:</span>
                                <span className="value">
                                  {getSafeValue(record, "capacity_percentage")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Número Ejes:</span>
                                <span className="value">
                                  {getSafeValue(record, "num_axes")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Propio:</span>
                                <span className="value">
                                  {renderBooleanValue(record.is_own)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Tercero:</span>
                                <span className="value">
                                  {renderBooleanValue(record.is_third_party)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Vinculado a:</span>
                                <span className="value">
                                  {getSafeValue(record, "linked_to")}
                                </span>
                              </div>
                            </div>

                            {/* Documentos Principales */}
                            <h4>Documentos del Vehículo</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">SOAT:</span>
                                <span className="value">
                                  {getSafeValue(record, "soat_number")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Vence SOAT:</span>
                                <span className="value">
                                  {formatDate(record.soat_expiry)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Tarjeta Operación:
                                </span>
                                <span className="value">
                                  {getSafeValue(record, "operation_card")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Tarjeta Op.:
                                </span>
                                <span className="value">
                                  {formatDate(record.operation_card_expiry)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Técnico-Mecánica:</span>
                                <span className="value">
                                  {getSafeValue(record, "technical_review")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Técnico-Mec.:
                                </span>
                                <span className="value">
                                  {formatDate(record.technical_review_expiry)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Segunda Columna - Más Documentos y Certificaciones */}
                          <div className={`record-section-${theme}`}>
                            <h4>Certificaciones y Seguros</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">
                                  Revisión Preventiva:
                                </span>
                                <span className="value">
                                  {getSafeValue(record, "preventive_review")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Vence Rev. Prev.:</span>
                                <span className="value">
                                  {formatDate(record.preventive_review_expiry)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Seguro Contractual:
                                </span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "contractual_insurance"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence S. Contractual:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.contractual_insurance_expiry
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Seguro Extracontractual:
                                </span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "extracontractual_insurance"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence S. Extracontractual:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.extracontractual_insurance_expiry
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Póliza Hidrocarburos:
                                </span>
                                <span className="value">
                                  {getSafeValue(record, "hydrocarbons_policy")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Póliza Hidrocarburos:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.hydrocarbons_policy_expiry
                                  )}
                                </span>
                              </div>
                            </div>

                            <h4>Certificaciones Técnicas</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">
                                  Cert. Línea de Vida:
                                </span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "lifeline_certification"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Línea de Vida:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.lifeline_certification_expiry
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Tabla de Aforo:</span>
                                <span className="value">
                                  {getSafeValue(record, "gauge_table")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Tabla de Aforo:
                                </span>
                                <span className="value">
                                  {formatDate(record.gauge_table_expiry)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Cert. King Pin:</span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "king_pin_certification"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Cert. King Pin:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.king_pin_certification_expiry
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Cert. Quinta Rueda:
                                </span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "fifth_wheel_certification"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Cert. Quinta Rueda:
                                </span>
                                <span className="value">
                                  {formatDate(
                                    record.fifth_wheel_certification_expiry
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Prueba Hidrostática:
                                </span>
                                <span className="value">
                                  {getSafeValue(record, "hydrostatic_test")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Prueba Hidrostática:
                                </span>
                                <span className="value">
                                  {formatDate(record.hydrostatic_test_expiry)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Prueba de Espesores:
                                </span>
                                <span className="value">
                                  {getSafeValue(record, "thickness_test")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Vence Prueba de Espesores:
                                </span>
                                <span className="value">
                                  {formatDate(record.thickness_test_expiry)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Tercera Columna - Operador e Información Adicional */}
                          <div className={`record-section-${theme}`}>
                            <h4>Referencia del Operador</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">Nombres:</span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "operator_first_names",
                                    "operator_name"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Apellidos:</span>
                                <span className="value">
                                  {getSafeValue(
                                    record,
                                    "operator_last_names",
                                    "operator_last_name"
                                  )}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Cédula:</span>
                                <span className="value">
                                  {getSafeValue(record, "operator_id_number")}
                                </span>
                              </div>
                            </div>
                            <p className="operator-reference-note">
                              Para ver información completa del operador, cambie
                              a la pestaña "Operadores".
                            </p>

                            {/* Información adicional que podrías mantener */}
                            <h4>Información Adicional</h4>
                            <div className={`details-grid-${theme}`}>
                              <div className="detail-item">
                                <span className="label">Sistema GPS:</span>
                                <span className="value">
                                  {getSafeValue(record, "gps_system")}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Tarjeta de Emergencia:
                                </span>
                                <span className="value">
                                  {renderBooleanValue(record.emergency_card)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <span className="label">
                                  Hoja de Seguridad:
                                </span>
                                <span className="value">
                                  {renderBooleanValue(record.safety_data_sheet)}
                                </span>
                              </div>
                              {/* Puedes mantener otros campos relevantes aquí */}
                            </div>
                          </div>

                          <div className={`record-actions-${theme}`}>
                            <button
                              className={`select-button-${theme} ${
                                isSelected(group.plate, record)
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                onSelectInspection(group.plate, record)
                              }
                            >
                              {isSelected(group.plate, record)
                                ? "Seleccionado"
                                : "Seleccionar este registro"}
                            </button>
                            <div className="source-info">
                              <small>Hoja: {record.source_sheet}</small>
                              <small>Archivo: {record.source_file}</small>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}

            {filteredData.length === 0 && (
              <tr className={`no-results-${theme}`}>
                <td colSpan="7">No se encontraron resultados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Función para formatear fechas mejorada
const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    // Verificar formato ISO
    if (
      typeof dateString === "string" &&
      /^\d{4}-\d{2}-\d{2}/.test(dateString)
    ) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("es-CO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      }
    }

    // Si no es formato ISO, intentar parsear como fecha genérica
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("es-CO");
    }

    // Si es un string sin parsear, devolverlo tal cual
    return dateString;
  } catch (e) {
    return String(dateString);
  }
};

export default InspectionDataTable;
